import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api';
import API_BASE_URL from '../apiConfig';
import './DepositStarsPage.css';
import { commonIcons } from './assetImports';
import { useTranslation } from 'react-i18next';

const DepositStarsPage = ({ isTelegramMiniApp, setWallets }) => {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation('depositWithdrawAll');
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const depositOptions = [100, 250, 500, 1000, 2500];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedAmount < 100) {
      setMessage(t('depositStars.minDepositError'));
      return;
    }
  
    setIsProcessing(true);
    setMessage('');
  
    try {
      const response = await apiRequest(`${API_BASE_URL}/api/request-stars-deposit`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount: selectedAmount })
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        setMessage(data.message || t('depositStars.requestFailed'));
        setIsProcessing(false);
        return;
      }
  
      setIsProcessing(false);
  
      if (!data.slug) {
        setMessage(t('depositStars.invoiceFailed'));
        return;
      }
  
      // Open invoice inside app
      await window.Telegram.WebApp.openInvoice(data.slug);
  
      // Listen for payment success
      window.Telegram.WebApp.onEvent('depositStars.paymentSuccessful', () => {
        console.log('Payment completed successfully!');
        setWallets(user.wallets);
      });
  
    } catch (error) {
      console.error('Error requesting deposit:', error);
      setMessage(t('depositStars.processingError'));
      setIsProcessing(false);
    }
  };

  const handleTrackClick = (e) => {
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    if (!clientX) return;
  
    const sliderTrack = document.querySelector(".depositStars-slider-track");
    const newAmount = calculateSelectedAmount(clientX, sliderTrack);
    setSelectedAmount(depositOptions[newAmount]);
  };

  const calculateSelectedAmount = (clientX, trackElement) => {
    const rect = trackElement.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const trackWidth = rect.width;
    const percent = (clickX / trackWidth) * 100;
    const adjustedPercent = (percent - 10) / 20; // 10% offset, 20% steps (for 5 options)
    return Math.round(Math.min(Math.max(adjustedPercent, 0), depositOptions.length - 1));
  };

  useEffect(() => {
    if (!isDragging) return;
  
    const handleMove = (e) => {
      e.preventDefault(); 
      e.stopPropagation();
  
      const clientX = e.clientX || e.touches?.[0]?.clientX;
      if (!clientX) return;
  
      const sliderTrack = document.querySelector(".depositStars-slider-track");
      const newAmount = calculateSelectedAmount(clientX, sliderTrack);
      setSelectedAmount(depositOptions[newAmount]);
    };
  
    const handleEnd = () => setIsDragging(false);
  
    const blockTouch = (e) => {
      if (e.target.closest(".depositStars-slider-track")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
  
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleEnd, { passive: false });
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchstart", blockTouch, { passive: false });
  
    return () => {
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchstart", blockTouch);
    };
  }, [isDragging]);

  useEffect(() => {
    const preventScroll = (e) => e.preventDefault();
    
    if (isTelegramMiniApp) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('touchmove', preventScroll, { passive: false });
    }
    
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [isTelegramMiniApp]);

  return (
    <div className="depositStars-page-container">
      <h2 className="depositStars-page-title">{t('depositStars.title')}</h2>

      <div className="depositStars-amount-display">
        <img src={commonIcons.starIcon} alt={t('depositStars.stars')} className="depositStars-currency-icon" />
        <span className="depositStars-selected-amount">{selectedAmount}</span>
      </div>

      <form onSubmit={handleSubmit} className="depositStars-form">
        <div className="depositStars-slider-container">
          <div 
            className="depositStars-slider-track"
            onMouseDown={() => setIsDragging(true)}
            onTouchStart={() => setIsDragging(true)}
            onPointerUp={handleTrackClick}
          >
            <div className="depositStars-slider-clickable-overlay" />
            <div className="depositStars-slider-dots">
              {depositOptions.map((_, index) => (
                <div key={index} className="depositStars-dot"></div>
              ))}
            </div>
            <div 
              className="depositStars-slider-handle" 
              style={{ 
                left: `calc(5% + ${(depositOptions.indexOf(selectedAmount) * 22.5)}%)`
              }}
            ></div>
          </div>
        </div>

        {message && <p className="depositStars-message">{message}</p>}

        <button
          type="submit"
          className="base-item-box depositStars-submit-button"
          disabled={isProcessing || selectedAmount === 0}
        >
          <span className="base-item-content depositStars-submit">
            {isProcessing ? (
              <>
                <span className="depositStars-spinner"></span> {t('depositStars.processing')}
              </>
            ) : t('depositStars.submitButton')}
          </span>
        </button>
      </form>
    </div>
  );
};

export default DepositStarsPage;